import { Button, DataGridRef, TooltipV2 } from '@cimplico/components';
import { useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import PageContainer from '../components/page-container/PageContainer';
import PageTitle from '../components/PageTitle';
import PrimaryPageActions from '../components/primary-page-actions/PrimaryPageActions';
import SecondaryPageActions from '../components/secondary-page-actions/SecondaryPageActions';
import SyncActiveWorkpapersConfirm from '../features/checklist-items/SyncActiveWorkpapersConfirm';
import ChecklistDefaultConfirm from '../features/checklists/ChecklistDefaultConfirm';
import ChecklistsDeleteConfirm from '../features/checklists/ChecklistDeleteConfirm';
import ChecklistsTable from '../features/checklists/ChecklistsTable';
import EditChecklistConfirm from '../features/checklists/EditChecklistConfirm';
import UpgradePrompt from '../components/upgrade-prompt/UpgradePrompt';
import { useChecklists } from '../queries/use-checklists';
import { ChecklistOverview } from '../types/checklist';
import { useFirmContext } from '../providers/FirmProvider';
import { SubscriptionTier } from '../types/firm';
import { useIsActiveSubscription } from '../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../hooks/use-is-firm-role';
import { useChecklist } from '../queries/use-checklist';
import { downloadChecklist } from '../features/checklist-items/utils';

function ChecklistsPage(): JSX.Element {
    const [action, setAction] = useState<string | undefined>();
    const [selectedChecklists, setSelectedChecklists] = useState<ChecklistOverview[]>([]);
    const navigate = useNavigate();
    const tableRef = useRef<DataGridRef | null>(null);

    const firm = useFirmContext();
    const { subscription, subscriptionVersion, shortId } = firm;
    const quota = subscriptionVersion === 1 ? firm.quota : null;

    const { isExternalUser } = useIsFirmRole();
    const activeSubscription = useIsActiveSubscription();
    const checklistQuery = useChecklist(selectedChecklists.length === 1 ? selectedChecklists[0].id : undefined);

    const tier = subscription?.tier;

    function redirectToNewChecklist(): void {
        const quotaReached = quota === null || quota.checklistLimit === null ? false : quota.checklistCount >= quota.checklistLimit;

        if (quotaReached) {
            setAction('UPGRADE');
            return;
        }

        navigate('new');
    }

    function clearAction(): void {
        setAction(undefined);
    }

    function resetTable(): void {
        clearAction();
        setSelectedChecklists([]);
        tableRef?.current?.clear();
        tableRef?.current?.resetPagination();
    }

    function downloadSelectedChecklist(): void {
        if (selectedChecklists.length !== 1) {
            return;
        }

        const checklist = checklistQuery.data?.checklist;

        if (!checklist) {
            return;
        }

        downloadChecklist(checklist);
    }

    const deleteChecklist = useMemo(() => selectedChecklists.filter((c) => !c.system && !c.default), [selectedChecklists]);
    const syncChecklists = useMemo(
        () => selectedChecklists.filter((c) => !c.system && new Date(c.updatedAt).getTime() > new Date(c.lastSynced).getTime()),
        [selectedChecklists]
    );

    const checklistsQuery = useChecklists({ search: '' });

    if (checklistsQuery.isLoading) {
        return <></>;
    }
    if (checklistsQuery.isError || !checklistsQuery.isSuccess) {
        return <p>Error!</p>;
    }

    const { checklists, totalItems } = checklistsQuery.data;

    if (isExternalUser) {
        return <Navigate to={`/${shortId}/dashboard`} />;
    }

    return (
        <PageContainer>
            {action === 'UPGRADE' && (
                <UpgradePrompt
                    targetTier={tier === SubscriptionTier.BASIC ? SubscriptionTier.ESSENTIAL : SubscriptionTier.PROFESSIONAL}
                    onClose={(): void => setAction(undefined)}
                    quota
                />
            )}
            {selectedChecklists.length === 1 && (
                <EditChecklistConfirm
                    checklist={selectedChecklists[0]}
                    isOpen={action === 'EDIT'}
                    onClose={clearAction}
                    onEdit={resetTable}
                />
            )}
            <ChecklistDefaultConfirm
                checklistId={selectedChecklists.length > 0 ? selectedChecklists[0].id : ''}
                isOpen={action === 'SET_DEFAULT'}
                onClose={clearAction}
                onSet={resetTable}
            />
            <ChecklistsDeleteConfirm
                checklists={deleteChecklist}
                isOpen={action === 'DELETE'}
                onClose={clearAction}
                onDelete={resetTable}
            />
            <SyncActiveWorkpapersConfirm
                checklistIds={syncChecklists.map((c) => c.id)}
                isOpen={action === 'SYNC'}
                onClose={clearAction}
                onSync={resetTable}
            />
            <div className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <PageTitle title="Checklists" />
                    <SecondaryPageActions maxActionsCount={4}>
                        {selectedChecklists.length === 1 && (
                            <>
                                <TooltipV2
                                    message="This checklist is currently the default checklist."
                                    disabled={!selectedChecklists[0].default}
                                    position="left"
                                >
                                    <Button
                                        size="sm"
                                        type="secondary"
                                        label="Set as Default"
                                        onClick={(): void => setAction('SET_DEFAULT')}
                                        disabled={!activeSubscription || selectedChecklists[0].default}
                                    />
                                </TooltipV2>
                                <TooltipV2
                                    message="System checklist cannot be edited."
                                    disabled={!selectedChecklists[0].system}
                                    position="left"
                                >
                                    <Button
                                        size="sm"
                                        type="secondary"
                                        label="Edit Checklist"
                                        onClick={(): void => setAction('EDIT')}
                                        disabled={!activeSubscription || selectedChecklists[0].system}
                                    />
                                </TooltipV2>
                                <Button
                                    size="sm"
                                    type="secondary"
                                    label="Download Checklist"
                                    onClick={downloadSelectedChecklist}
                                    disabled={!activeSubscription}
                                />
                            </>
                        )}
                        {selectedChecklists.length > 0 && (
                            <>
                                <TooltipV2
                                    message={
                                        selectedChecklists.length - syncChecklists.length > 1
                                            ? 'No available checklists can be synced.'
                                            : selectedChecklists[0].system
                                            ? 'System checklist cannot be synced.'
                                            : 'Checklist is synced with open workpapers.'
                                    }
                                    disabled={syncChecklists.length > 0}
                                    position="left"
                                >
                                    <Button
                                        type="secondary"
                                        label={`Sync to Open Workpapers (${syncChecklists.length})`}
                                        onClick={(): void => setAction('SYNC')}
                                        disabled={!activeSubscription || syncChecklists.length === 0}
                                        size="sm"
                                    />
                                </TooltipV2>
                                <TooltipV2
                                    message="Default / System checklists cannot be deleted."
                                    disabled={deleteChecklist.length > 0}
                                    position="left"
                                >
                                    <Button
                                        size="sm"
                                        type="danger"
                                        label={`Delete (${deleteChecklist.length})`}
                                        onClick={(): void => setAction('DELETE')}
                                        disabled={!activeSubscription || deleteChecklist.length === 0}
                                    />
                                </TooltipV2>
                            </>
                        )}
                    </SecondaryPageActions>
                    {selectedChecklists.length === 0 && (
                        <PrimaryPageActions
                            filterOptions={[]}
                            sortOptions={[]}
                            id="checklist-actions"
                            primaryButtonLabel="New Checklist"
                            onPrimaryButtonClick={redirectToNewChecklist}
                            primaryButtonTooltipPosition="left"
                            onFilterChange={console.log}
                            onSearchChange={console.log}
                            onSortChange={console.log}
                            disablePrimaryButton={!activeSubscription}
                            disableSecondaryButton={!activeSubscription}
                        />
                    )}
                </div>
                <section className="mt-4 mx-auto">
                    <ChecklistsTable checklists={checklists} totalItems={totalItems} onSelect={setSelectedChecklists} tableRef={tableRef} />
                </section>
            </div>
        </PageContainer>
    );
}

export default ChecklistsPage;
